/**
 * Dev Console → Arche Management → Store: the public Arche Store's controls.
 *
 * The switch: shows the new store in this environment at all (its pages, its
 * API and the header's link). While it is off, "Arche Store" is the old one.
 *
 * Featured apps: the Apps tab's hero — up to twelve picks from the store, in
 * order, each with an optional kicker, one line, and wide hero art.
 *
 * Today, stories and collections: Fias-written stories, collections of apps
 * (each with an optional note per app), and daily picks — a pool whose item
 * turns over every UTC day ("App of the Day"). A place on Today, or as a
 * shelf on the Apps tab, is a number; blank keeps it off. A story's address is
 * set when it is created and never changes.
 *
 * Discover: the search page's suggested words.
 *
 * Hidden from the Store: keep an arche or one listing off the store (test
 * arches, platform plumbing) with a reason, undoably. Not a takedown:
 * restriction and disable remain the safety tools.
 *
 * Screenshots: the queue of screenshots the image screen was unsure of, and
 * the screenshots of Fias's built-in arches (StoreScreenshotsAdmin.tsx).
 *
 * Uncategorized: listed apps no category page shows, to chase.
 *
 * Every save goes to one writer on the server, which refuses anything not on
 * the store and audits the change; the snapshot pages follow within a run.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowDown,
  ArrowUp,
  EyeOff,
  GripVertical,
  ImagePlus,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import type { StoreCard } from '@fias/public-site-ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { useConfirm } from '@/components/ui/confirm-dialog';
import api, {
  ApiError,
  type AdminStoreCollection,
  type AdminStoreCollectionInput,
  type AdminStoreCollectionKind,
  type AdminStoreFeaturedPick,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { refreshStoreAvailability } from '@/lib/store-availability';
import { BuiltInArcheScreenshotsCard, ScreenshotReviewQueueCard } from './StoreScreenshotsAdmin';

/** Must match the server (STORE_* in @fias/db-types/store-catalog). */
const FEATURED_MAX = 12;
const LABEL_MAX = 32;
const TAGLINE_MAX = 120;
const COLLECTION_TITLE_MAX = 80;
const COLLECTION_KICKER_MAX = 32;
const COLLECTION_TAGLINE_MAX = 120;
const COLLECTION_BODY_MAX = 5000;
const COLLECTION_ITEMS_MAX = 40;
const COLLECTION_BLURB_MAX = 200;
const SUGGESTION_MAX = 40;
const SUGGESTIONS_MAX = 12;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Must match the server (GAMES_HERO_ART_* in @fias/db-types). */
const HERO_ART_TYPES = 'image/png,image/jpeg,image/webp';

const KIND_LABEL: Record<AdminStoreCollectionKind, string> = {
  story: 'Story',
  collection: 'Collection',
  daily: 'Daily pick',
};

function itemKey(archId: string, listingId: string | null | undefined): string {
  return `${archId}:${listingId ?? ''}`;
}

function errorMessage(err: unknown): string {
  return err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong';
}

/** How a picked item is described under its title. */
function describe(item: { isGame?: boolean; listingId: string | null; archeName?: string }) {
  const kind = item.isGame ? 'Game' : 'App';
  return item.listingId && item.archeName ? `${kind} in ${item.archeName}` : kind;
}

/** Upload hero art and have the server write it; its key goes on the pick or story. */
async function uploadStoreHeroArt(file: File): Promise<{ heroKey: string; heroUrl: string }> {
  const { uploadUrl, stagingKey } = await api.adminGetStoreHeroArtUploadUrl({
    mimeType: file.type,
    sizeBytes: file.size,
  });
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (status ${response.status})`);
  const written = await api.adminWriteStoreHeroArt(stagingKey);
  return { heroKey: written.heroKey, heroUrl: written.heroUrlSmall };
}

/** Everything on the store (apps and games), shared by every picker on this page. */
function useStorePicker() {
  return useQuery({
    queryKey: queryKeys.store.adminPicker(),
    queryFn: () => api.adminGetStorePicker(),
  });
}

function ItemIcon({ url }: { url: string | null }): ReactElement {
  return url ? (
    <img src={url} alt="" className="h-7 w-7 rounded-md" />
  ) : (
    <div className="h-7 w-7 rounded-md bg-muted" />
  );
}

/** Search the store and pick an item; already-picked ones are left out. */
function ItemPicker({
  items,
  excluded,
  onPick,
  disabled,
  label,
  actionLabel,
}: {
  items: StoreCard[];
  excluded: Set<string>;
  onPick: (card: StoreCard) => void;
  disabled?: boolean;
  label: string;
  actionLabel: (card: StoreCard) => string;
}): ReactElement {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const matches = items
    .filter((card) => !excluded.has(itemKey(card.archId, card.listingId)))
    .filter((card) => !q || `${card.title} ${card.archeName}`.toLowerCase().includes(q))
    .slice(0, q ? 12 : 8);
  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={`${label}...`}
          aria-label={label}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9"
        />
      </div>
      {matches.map((card) => (
        <div key={card.ref} className="flex items-center gap-2 rounded-md p-2 hover:bg-muted">
          <ItemIcon url={card.iconUrl} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{card.title}</div>
            <div className="truncate text-xs text-muted-foreground">{describe(card)}</div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onPick(card)}
            disabled={disabled}
            aria-label={actionLabel(card)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {matches.length === 0 && (
        <p className="py-2 text-center text-sm text-muted-foreground">Nothing matches</p>
      )}
    </div>
  );
}

/** Hero art: a preview, and add/replace/remove. */
function HeroArtField({
  name,
  heroUrl,
  onChange,
}: {
  name: string;
  heroUrl: string | null;
  onChange: (art: { heroKey: string; heroUrl: string } | null) => void;
}): ReactElement {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const choose = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadStoreHeroArt(file));
    } catch (err) {
      toast({
        title: 'Hero art not added',
        description: errorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-2">
      {heroUrl ? (
        <img src={heroUrl} alt={`Hero art for ${name}`} className="h-9 w-16 rounded object-cover" />
      ) : (
        <span className="text-xs text-muted-foreground">No hero art: the card shows the icon</span>
      )}
      <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary">
        {uploading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <ImagePlus className="h-3 w-3" />
        )}
        {heroUrl ? 'Replace hero art' : 'Add hero art'}
        <input
          type="file"
          accept={HERO_ART_TYPES}
          className="sr-only"
          aria-label={`Hero art for ${name}`}
          disabled={uploading}
          onChange={(e) => {
            void choose(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </label>
      {heroUrl && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => onChange(null)}
        >
          Remove art
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The switch
// ---------------------------------------------------------------------------

function StorePageSwitchCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const setting = useQuery({
    queryKey: queryKeys.store.adminSwitch(),
    queryFn: () => api.adminGetStorePage(),
  });
  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => api.adminSetStorePage(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.store.adminSwitch(), data);
      void refreshStoreAvailability();
      toast({ title: data.enabled ? 'Arche Store shown' : 'Arche Store hidden' });
    },
    onError: (err) =>
      toast({
        title: 'Could not change the switch',
        description: errorMessage(err),
        variant: 'destructive',
      }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Arche Store</CardTitle>
        <CardDescription>
          Shows or hides the new public Arche Store in this environment — its pages, its catalog and
          search, and the header’s link to it. While it is off, “Arche Store” is the old store
          (Advanced search). Off until someone turns it on; audited. Other servers follow within
          half a minute, open pages within five.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="store-page-enabled">Show the new Arche Store</Label>
          <Switch
            id="store-page-enabled"
            aria-label="Show the new Arche Store"
            checked={setting.data?.enabled === true}
            disabled={setting.isLoading || setEnabled.isPending}
            onCheckedChange={(checked) => setEnabled.mutate(checked)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Featured apps
// ---------------------------------------------------------------------------

interface FeaturedPick extends AdminStoreFeaturedPick {
  key: string;
  title: string;
  subtitle: string;
  iconUrl: string | null;
  onStore: boolean;
  heroUrl: string | null;
}

function SortableFeaturedPick({
  pick,
  onChange,
  onRemove,
}: {
  pick: FeaturedPick;
  onChange: (next: FeaturedPick) => void;
  onRemove: () => void;
}): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: pick.key,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="space-y-2 rounded-md border bg-card p-2"
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab p-1 text-muted-foreground"
          aria-label={`Move ${pick.title}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <ItemIcon url={pick.iconUrl} />
        <span className="flex-1 truncate text-sm font-medium">{pick.title}</span>
        {!pick.onStore && (
          <Badge variant="outline" className="text-xs">
            Not on the store
          </Badge>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onRemove}
          aria-label={`Remove ${pick.title}`}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-2 pl-8 sm:grid-cols-[1fr_2fr]">
        <Input
          aria-label={`Kicker for ${pick.title}`}
          placeholder="Kicker (e.g. New app)"
          maxLength={LABEL_MAX}
          value={pick.label ?? ''}
          onChange={(e) => onChange({ ...pick, label: e.target.value })}
          className="h-8 text-sm"
        />
        <Input
          aria-label={`Tagline for ${pick.title}`}
          placeholder="One line under the title"
          maxLength={TAGLINE_MAX}
          value={pick.tagline ?? ''}
          onChange={(e) => onChange({ ...pick, tagline: e.target.value })}
          className="h-8 text-sm"
        />
      </div>
      <div className="pl-8">
        <HeroArtField
          name={pick.title}
          heroUrl={pick.heroUrl}
          onChange={(art) =>
            onChange({ ...pick, heroKey: art?.heroKey ?? null, heroUrl: art?.heroUrl ?? null })
          }
        />
      </div>
    </div>
  );
}

function FeaturedAppsCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [draft, setDraft] = useState<FeaturedPick[] | null>(null);

  const featured = useQuery({
    queryKey: queryKeys.store.adminFeatured(),
    queryFn: () => api.adminGetStoreFeatured(),
  });
  const picker = useStorePicker();

  const cardsByKey = useMemo(() => {
    const map = new Map<string, StoreCard>();
    for (const card of picker.data?.items ?? [])
      map.set(itemKey(card.archId, card.listingId), card);
    return map;
  }, [picker.data]);

  const saved: FeaturedPick[] = useMemo(
    () =>
      (featured.data?.featured ?? []).map((row) => {
        const key = itemKey(row.archId, row.listingId);
        const card = cardsByKey.get(key);
        return {
          key,
          archId: row.archId,
          listingId: row.listingId,
          label: row.label,
          tagline: row.tagline,
          heroKey: row.heroKey,
          heroUrl: row.heroUrl,
          title: row.title ?? card?.title ?? row.archId,
          subtitle: card ? describe(card) : '',
          iconUrl: card?.iconUrl ?? null,
          onStore: row.onStore,
        };
      }),
    [featured.data, cardsByKey],
  );
  const picks = draft ?? saved;

  const save = useMutation({
    mutationFn: (list: FeaturedPick[]) =>
      api.adminSetStoreFeatured(
        list.map((p) => ({
          archId: p.archId,
          listingId: p.listingId ?? null,
          label: p.label?.trim() || null,
          tagline: p.tagline?.trim() || null,
          heroKey: p.heroKey ?? null,
        })),
      ),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
      toast({ title: 'Featured apps saved' });
    },
    onError: (err) =>
      toast({ title: 'Not saved', description: errorMessage(err), variant: 'destructive' }),
  });

  const add = (card: StoreCard) => {
    if (picks.length >= FEATURED_MAX) return;
    setDraft([
      ...picks,
      {
        key: itemKey(card.archId, card.listingId),
        archId: card.archId,
        listingId: card.listingId,
        label: null,
        tagline: null,
        heroKey: null,
        heroUrl: null,
        title: card.title,
        subtitle: describe(card),
        iconUrl: card.iconUrl,
        onStore: true,
      },
    ]);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = picks.findIndex((p) => p.key === active.id);
    const to = picks.findIndex((p) => p.key === over.id);
    setDraft(arrayMove(picks, from, to));
  };

  if (featured.isLoading || picker.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (featured.isError || picker.isError) {
    return (
      <p className="text-sm text-destructive">
        Featured apps didn’t load: {errorMessage(featured.error ?? picker.error)}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">On the Arche Store</CardTitle>
          <CardDescription>Click + to feature an app (or a game) on the Apps tab.</CardDescription>
        </CardHeader>
        <CardContent className="max-h-[540px] overflow-y-auto">
          <ItemPicker
            items={picker.data?.items ?? []}
            excluded={new Set(picks.map((p) => p.key))}
            onPick={add}
            disabled={picks.length >= FEATURED_MAX}
            label="Find an app to feature"
            actionLabel={(card) => `Feature ${card.title}`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-base">
                Featured apps ({picks.length}/{FEATURED_MAX})
              </CardTitle>
              <CardDescription>
                Drag to reorder. With none, the Apps tab features its top apps.
              </CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => save.mutate(picks)}
              disabled={draft === null || save.isPending}
              className="gap-1.5"
              aria-label="Save featured apps"
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="max-h-[540px] space-y-2 overflow-y-auto">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={picks.map((p) => p.key)} strategy={verticalListSortingStrategy}>
              {picks.map((pick) => (
                <SortableFeaturedPick
                  key={pick.key}
                  pick={pick}
                  onChange={(next) => setDraft(picks.map((p) => (p.key === next.key ? next : p)))}
                  onRemove={() => setDraft(picks.filter((p) => p.key !== pick.key))}
                />
              ))}
            </SortableContext>
          </DndContext>
          {picks.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              Nothing featured. Add apps from the left.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Today, stories and collections
// ---------------------------------------------------------------------------

interface EditorItem {
  key: string;
  archId: string;
  listingId: string | null;
  blurb: string;
  title: string;
  subtitle: string;
  onStore: boolean;
}

interface EditorState {
  collectionId: string | null;
  slug: string;
  kind: AdminStoreCollectionKind;
  live: boolean;
  title: string;
  kicker: string;
  tagline: string;
  body: string;
  heroKey: string | null;
  heroUrl: string | null;
  todayOrder: string;
  appsOrder: string;
  items: EditorItem[];
}

const NEW_KICKER: Record<AdminStoreCollectionKind, string> = {
  story: '',
  collection: '',
  daily: 'App of the Day',
};

function newEditor(kind: AdminStoreCollectionKind): EditorState {
  return {
    collectionId: null,
    slug: '',
    kind,
    live: false,
    title: '',
    kicker: NEW_KICKER[kind],
    tagline: '',
    body: '',
    heroKey: null,
    heroUrl: null,
    todayOrder: '',
    appsOrder: '',
    items: [],
  };
}

function editorFrom(c: AdminStoreCollection, cards: Map<string, StoreCard>): EditorState {
  return {
    collectionId: c.collectionId,
    slug: c.slug,
    kind: c.kind,
    live: c.status === 'live',
    title: c.title,
    kicker: c.kicker ?? '',
    tagline: c.tagline ?? '',
    body: c.body ?? '',
    heroKey: c.heroKey,
    heroUrl: c.heroUrl,
    todayOrder: c.todayOrder === null ? '' : String(c.todayOrder),
    appsOrder: c.appsOrder === null ? '' : String(c.appsOrder),
    items: c.items.map((item) => {
      const key = itemKey(item.archId, item.listingId);
      const card = cards.get(key);
      return {
        key,
        archId: item.archId,
        listingId: item.listingId,
        blurb: item.blurb ?? '',
        title: item.title ?? card?.title ?? item.archId,
        subtitle: card ? describe(card) : '',
        onStore: item.onStore,
      };
    }),
  };
}

/** A place number from the field, or null when it is blank. */
function order(value: string): number | null {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 1 ? Math.min(n, 1000) : null;
}

function toInput(e: EditorState): AdminStoreCollectionInput {
  return {
    ...(e.collectionId ? { collectionId: e.collectionId } : {}),
    slug: e.slug.trim(),
    kind: e.kind,
    status: e.live ? 'live' : 'draft',
    title: e.title.trim(),
    kicker: e.kicker.trim() || null,
    tagline: e.tagline.trim() || null,
    body: e.kind === 'daily' ? null : e.body.trim() || null,
    heroKey: e.heroKey,
    todayOrder: order(e.todayOrder),
    appsOrder: e.kind === 'daily' ? null : order(e.appsOrder),
    items: e.items.map((i) => ({
      archId: i.archId,
      listingId: i.listingId,
      blurb: i.blurb.trim() || null,
    })),
  };
}

function CollectionEditor({
  initial,
  cards,
  onDone,
}: {
  initial: EditorState;
  cards: StoreCard[];
  onDone: () => void;
}): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [e, setE] = useState<EditorState>(initial);
  const set = (patch: Partial<EditorState>) => setE((prev) => ({ ...prev, ...patch }));
  const isNew = e.collectionId === null;
  const slugValid = SLUG_RE.test(e.slug.trim());

  const save = useMutation({
    mutationFn: () => api.adminSaveStoreCollection(toInput(e)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
      toast({ title: `${KIND_LABEL[e.kind]} saved` });
      onDone();
    },
    onError: (err) =>
      toast({ title: 'Not saved', description: errorMessage(err), variant: 'destructive' }),
  });

  const move = (index: number, by: -1 | 1) => {
    const to = index + by;
    if (to < 0 || to >= e.items.length) return;
    set({ items: arrayMove(e.items, index, to) });
  };

  const add = (card: StoreCard) => {
    if (e.items.length >= COLLECTION_ITEMS_MAX) return;
    set({
      items: [
        ...e.items,
        {
          key: itemKey(card.archId, card.listingId),
          archId: card.archId,
          listingId: card.listingId,
          blurb: '',
          title: card.title,
          subtitle: describe(card),
          onStore: true,
        },
      ],
    });
  };

  const name = e.title.trim() || KIND_LABEL[e.kind].toLowerCase();

  return (
    <Card className="border-primary/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {isNew ? `New ${KIND_LABEL[e.kind].toLowerCase()}` : `Edit “${initial.title}”`}
        </CardTitle>
        <CardDescription>
          {e.kind === 'story' &&
            'A story: Fias-written text with the apps it is about. It has its own page.'}
          {e.kind === 'collection' &&
            'A collection: a list of apps, each with an optional note. It has its own page, and can be a shelf on the Apps tab.'}
          {e.kind === 'daily' &&
            'A daily pick: a pool of apps (or games). Today shows one each UTC day, in turn.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="collection-slug">Address</Label>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <span className="shrink-0">/a/store/story/</span>
                <Input
                  id="collection-slug"
                  value={e.slug}
                  maxLength={80}
                  disabled={!isNew}
                  placeholder="get-organised"
                  onChange={(ev) => set({ slug: ev.target.value.toLowerCase() })}
                  className="h-8"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {isNew
                  ? 'Lowercase words and dashes. It can’t be changed once saved.'
                  : 'Fixed: links to it are shared and indexed.'}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="collection-title">Title</Label>
              <Input
                id="collection-title"
                value={e.title}
                maxLength={COLLECTION_TITLE_MAX}
                onChange={(ev) => set({ title: ev.target.value })}
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="collection-kicker">Kicker</Label>
              <Input
                id="collection-kicker"
                value={e.kicker}
                maxLength={COLLECTION_KICKER_MAX}
                placeholder={e.kind === 'daily' ? 'App of the Day' : 'e.g. Get started'}
                onChange={(ev) => set({ kicker: ev.target.value })}
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="collection-tagline">One line</Label>
              <Input
                id="collection-tagline"
                value={e.tagline}
                maxLength={COLLECTION_TAGLINE_MAX}
                onChange={(ev) => set({ tagline: ev.target.value })}
                className="h-8"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="collection-today">Place on Today</Label>
              <Input
                id="collection-today"
                inputMode="numeric"
                value={e.todayOrder}
                placeholder="Blank: not on Today"
                onChange={(ev) => set({ todayOrder: ev.target.value.replace(/\D/g, '') })}
                className="h-8"
              />
            </div>
            {e.kind !== 'daily' && (
              <div className="space-y-1">
                <Label htmlFor="collection-apps">Shelf on the Apps tab</Label>
                <Input
                  id="collection-apps"
                  inputMode="numeric"
                  value={e.appsOrder}
                  placeholder="Blank: no shelf"
                  onChange={(ev) => set({ appsOrder: ev.target.value.replace(/\D/g, '') })}
                  className="h-8"
                />
              </div>
            )}
          </div>

          {e.kind !== 'daily' && (
            <div className="space-y-1">
              <Label htmlFor="collection-body">
                {e.kind === 'story' ? 'The story' : 'Introduction (optional)'}
              </Label>
              <Textarea
                id="collection-body"
                value={e.body}
                maxLength={COLLECTION_BODY_MAX}
                rows={e.kind === 'story' ? 8 : 3}
                onChange={(ev) => set({ body: ev.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Plain text; leave a blank line between paragraphs. {e.body.length}/
                {COLLECTION_BODY_MAX}
              </p>
            </div>
          )}

          <HeroArtField
            name={name}
            heroUrl={e.heroUrl}
            onChange={(art) =>
              set({ heroKey: art?.heroKey ?? null, heroUrl: art?.heroUrl ?? null })
            }
          />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <Label>
                {e.kind === 'daily' ? 'The pool' : 'Apps'} ({e.items.length}/{COLLECTION_ITEMS_MAX})
              </Label>
              {e.items.length === 0 && (
                <p className="text-sm text-muted-foreground">None yet. Add some from the right.</p>
              )}
              {e.items.map((item, index) => (
                <div key={item.key} className="space-y-1 rounded-md border p-2">
                  <div className="flex items-center gap-1">
                    <span className="flex-1 truncate text-sm font-medium">{item.title}</span>
                    {!item.onStore && (
                      <Badge variant="outline" className="text-xs">
                        Not on the store
                      </Badge>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label={`Move ${item.title} up`}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      disabled={index === e.items.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label={`Move ${item.title} down`}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => set({ items: e.items.filter((i) => i.key !== item.key) })}
                      aria-label={`Remove ${item.title}`}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  {e.kind !== 'daily' && (
                    <Input
                      aria-label={`Note for ${item.title}`}
                      placeholder="A note (optional)"
                      maxLength={COLLECTION_BLURB_MAX}
                      value={item.blurb}
                      onChange={(ev) =>
                        set({
                          items: e.items.map((i) =>
                            i.key === item.key ? { ...i, blurb: ev.target.value } : i,
                          ),
                        })
                      }
                      className="h-8 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>
            <ItemPicker
              items={cards}
              excluded={new Set(e.items.map((i) => i.key))}
              onPick={add}
              disabled={e.items.length >= COLLECTION_ITEMS_MAX}
              label="Find an app to add"
              actionLabel={(card) => `Add ${card.title}`}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
            <div className="flex items-center gap-2">
              <Switch
                id="collection-live"
                checked={e.live}
                onCheckedChange={(checked) => set({ live: checked })}
              />
              <Label htmlFor="collection-live">
                {e.live ? 'Live on the store' : 'Draft (not shown)'}
              </Label>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onDone}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="gap-1.5"
                disabled={!slugValid || !e.title.trim() || save.isPending}
                aria-label={`Save ${KIND_LABEL[e.kind].toLowerCase()}`}
              >
                {save.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function CollectionsCard(): ReactElement {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EditorState | null>(null);
  const collections = useQuery({
    queryKey: queryKeys.store.adminCollections(),
    queryFn: () => api.adminGetStoreCollections(),
  });
  const picker = useStorePicker();
  const cardsByKey = useMemo(() => {
    const map = new Map<string, StoreCard>();
    for (const card of picker.data?.items ?? [])
      map.set(itemKey(card.archId, card.listingId), card);
    return map;
  }, [picker.data]);

  const remove = useMutation({
    mutationFn: (collectionId: string) => api.adminDeleteStoreCollection(collectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
      toast({ title: 'Deleted' });
    },
    onError: (err) =>
      toast({ title: 'Not deleted', description: errorMessage(err), variant: 'destructive' }),
  });

  // Today's order first, then the rest by when they last changed.
  const list = [...(collections.data?.collections ?? [])].sort(
    (a, b) => (a.todayOrder ?? 1e6) - (b.todayOrder ?? 1e6),
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Today, stories and collections</CardTitle>
              <CardDescription>
                What Today shows, in its order, and the collections that are shelves on the Apps
                tab. With nothing on Today, it shows the top apps and games.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {(['story', 'collection', 'daily'] as const).map((kind) => (
                <Button
                  key={kind}
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={editing !== null}
                  onClick={() => setEditing(newEditor(kind))}
                >
                  <Plus className="h-4 w-4" />
                  {KIND_LABEL[kind]}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {collections.isLoading && <Loader2 className="h-5 w-5 animate-spin" />}
          {collections.isError && (
            <p className="text-sm text-destructive">
              Didn’t load: {errorMessage(collections.error)}
            </p>
          )}
          {collections.data && list.length === 0 && (
            <p className="text-sm text-muted-foreground">No stories or collections yet.</p>
          )}
          {list.length > 0 && (
            <ul className="divide-y rounded-md border">
              {list.map((c) => (
                <li key={c.collectionId} className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium">{c.title}</span>
                      <Badge variant="secondary" className="text-xs">
                        {KIND_LABEL[c.kind]}
                      </Badge>
                      {c.status === 'draft' && (
                        <Badge variant="outline" className="text-xs">
                          Draft
                        </Badge>
                      )}
                      {c.todayOrder !== null && (
                        <Badge variant="outline" className="text-xs">
                          Today #{c.todayOrder}
                        </Badge>
                      )}
                      {c.appsOrder !== null && (
                        <Badge variant="outline" className="text-xs">
                          Apps shelf #{c.appsOrder}
                        </Badge>
                      )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {c.kind === 'daily' ? 'Daily pick' : `/a/store/story/${c.slug}`} ·{' '}
                      {c.items.length} item{c.items.length === 1 ? '' : 's'}
                      {c.items.some((i) => !i.onStore) && ' · some are no longer on the store'}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={editing !== null}
                    onClick={() => setEditing(editorFrom(c, cardsByKey))}
                    aria-label={`Edit ${c.title}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={remove.isPending}
                    onClick={() => {
                      void confirm({
                        title: `Delete “${c.title}”?`,
                        description:
                          c.kind === 'daily'
                            ? 'It leaves Today. This can’t be undone.'
                            : 'Its page goes with it, and links to it stop working. This can’t be undone.',
                        confirmLabel: 'Delete',
                        destructive: true,
                      }).then((ok) => {
                        if (ok) remove.mutate(c.collectionId);
                      });
                    }}
                    aria-label={`Delete ${c.title}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      {editing && (
        <CollectionEditor
          key={editing.collectionId ?? `new-${editing.kind}`}
          initial={editing}
          cards={picker.data?.items ?? []}
          onDone={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discover words
// ---------------------------------------------------------------------------

function SuggestionsCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const saved = useQuery({
    queryKey: queryKeys.store.adminSuggestions(),
    queryFn: () => api.adminGetStoreSuggestions(),
  });
  const text = draft ?? (saved.data?.suggestions ?? []).join('\n');
  const phrases = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const tooLong = phrases.find((p) => p.length > SUGGESTION_MAX);
  const save = useMutation({
    mutationFn: () => api.adminSetStoreSuggestions(phrases),
    onSuccess: () => {
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
      toast({ title: 'Discover words saved' });
    },
    onError: (err) =>
      toast({ title: 'Not saved', description: errorMessage(err), variant: 'destructive' }),
  });
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Discover</CardTitle>
        <CardDescription>
          The words the search page suggests before anyone types — one per line, up to{' '}
          {SUGGESTIONS_MAX}, in order.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Textarea
          aria-label="Discover words"
          rows={6}
          value={text}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={'budget\nnotes\nphoto editor'}
        />
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {tooLong
              ? `“${tooLong.slice(0, 20)}…” is longer than ${SUGGESTION_MAX} characters.`
              : `${phrases.length}/${SUGGESTIONS_MAX}`}
          </p>
          <Button
            size="sm"
            className="gap-1.5"
            aria-label="Save Discover words"
            disabled={
              draft === null || save.isPending || !!tooLong || phrases.length > SUGGESTIONS_MAX
            }
            onClick={() => save.mutate()}
          >
            <Save className="h-4 w-4" />
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Hidden from the store
// ---------------------------------------------------------------------------

function HiddenFromStoreCard(): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<StoreCard | null>(null);
  const [reason, setReason] = useState('');
  const hidden = useQuery({
    queryKey: queryKeys.store.adminHidden(),
    queryFn: () => api.adminGetStoreHidden(),
  });
  const picker = useStorePicker();

  const change = useMutation({
    mutationFn: (input: Parameters<typeof api.adminSetStoreHidden>[0]) =>
      api.adminSetStoreHidden(input),
    onSuccess: (_result, input) => {
      setChosen(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.store.all });
      toast({
        title: input.hidden ? 'Hidden from the Arche Store' : 'Shown on the Arche Store again',
        description: 'The store’s pages update within a few minutes.',
      });
    },
    onError: (err) =>
      toast({ title: 'Not changed', description: errorMessage(err), variant: 'destructive' }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Hidden from the Arche Store</CardTitle>
        <CardDescription>
          Keeps an arche (or one listing) off the store — test arches, platform plumbing — without
          touching the arche, the Advanced search or the Games page. To take something down
          everywhere, restrict or disable the arche instead.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {chosen ? (
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              change.mutate({
                archId: chosen.archId,
                listingId: chosen.listingId,
                hidden: true,
                reason: reason.trim(),
              });
            }}
          >
            <span className="self-center text-sm font-medium">{chosen.title}</span>
            <Input
              aria-label={`Why hide ${chosen.title}`}
              placeholder="Why (for the audit log)"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-9 flex-1"
            />
            <Button
              type="submit"
              size="sm"
              className="gap-1.5"
              disabled={!reason.trim() || change.isPending}
            >
              <EyeOff className="h-4 w-4" />
              Hide from the store
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setChosen(null)}>
              Cancel
            </Button>
          </form>
        ) : (
          <ItemPicker
            items={picker.data?.items ?? []}
            excluded={new Set()}
            onPick={setChosen}
            label="Find something to hide"
            actionLabel={(card) => `Hide ${card.title}`}
          />
        )}

        {hidden.isError && (
          <p className="text-sm text-destructive">Didn’t load: {errorMessage(hidden.error)}</p>
        )}
        {hidden.data && hidden.data.hidden.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing is hidden.</p>
        )}
        {hidden.data && hidden.data.hidden.length > 0 && (
          <ul className="divide-y rounded-md border">
            {hidden.data.hidden.map((item) => (
              <li
                key={itemKey(item.archId, item.listingId)}
                className="flex items-center gap-3 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {item.title ?? item.archId}
                    {item.listingId && (
                      <span className="font-normal text-muted-foreground"> (a listing)</span>
                    )}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {item.reason} · {new Date(item.hiddenAt).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={change.isPending}
                  onClick={() =>
                    change.mutate({ archId: item.archId, listingId: item.listingId, hidden: false })
                  }
                >
                  Show again
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Uncategorized apps
// ---------------------------------------------------------------------------

function UncategorizedAppsCard(): ReactElement {
  const apps = useQuery({
    queryKey: queryKeys.store.adminUncategorized(),
    queryFn: () => api.adminGetStoreUncategorized(),
  });
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Uncategorized apps{apps.data ? ` (${apps.data.apps.length})` : ''}
        </CardTitle>
        <CardDescription>
          On the store, in search and the charts, but on no category page. A plugin gets its
          categories from its manifest (<code>store.categories</code>) at its next publish.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {apps.isError && (
          <p className="text-sm text-destructive">Didn’t load: {errorMessage(apps.error)}</p>
        )}
        {apps.data && apps.data.apps.length === 0 && (
          <p className="text-sm text-muted-foreground">Every app has a category.</p>
        )}
        {apps.data && apps.data.apps.length > 0 && (
          <ul className="max-h-[320px] divide-y overflow-y-auto rounded-md border">
            {apps.data.apps.map((app) => (
              <li key={itemKey(app.archId, app.listingId)} className="p-2 text-sm">
                <span className="font-medium">{app.title}</span>{' '}
                <span className="text-xs text-muted-foreground">
                  {app.listingId ? `listing in ${app.archeName}` : app.archId}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function StoreAdminPanel(): ReactElement {
  return (
    <div className="space-y-6">
      <StorePageSwitchCard />
      <FeaturedAppsCard />
      <CollectionsCard />
      <ScreenshotReviewQueueCard />
      <BuiltInArcheScreenshotsCard />
      <SuggestionsCard />
      <HiddenFromStoreCard />
      <UncategorizedAppsCard />
    </div>
  );
}
